
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Building2,
  CreditCard,
  Calendar,
  AlertTriangle,
  Loader2,
  ExternalLink,
  RefreshCw,
  X,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  useTenantSubscription,
  useTenantPricing,
  useUpdateTenantSubscription,
  useCancelTenantSubscription,
  useReactivateTenantSubscription,
  useStripePortal,
} from '@/api/tenants';
import { cn } from '@/lib/utils';

export function SubscriptionManagement() {
  const { t } = useTranslation();
  const { toast } = useToast();

  // State for dialogs
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [newGymCount, setNewGymCount] = useState<number>(1);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelImmediate, setCancelImmediate] = useState(false);

  // API hooks
  const {
    data: subscription,
    isLoading,
    error,
    refetch,
  } = useTenantSubscription();
  const { data: pricing } = useTenantPricing();
  const updateMutation = useUpdateTenantSubscription();
  const cancelMutation = useCancelTenantSubscription();
  const reactivateMutation = useReactivateTenantSubscription();
  const portalMutation = useStripePortal();

  const getStatusBadge = (status: string) => {
    const variants: Record<
      string,
      {
        variant: 'default' | 'secondary' | 'destructive' | 'outline';
        label: string;
      }
    > = {
      active: { variant: 'default', label: t('subscription.status.active') },
      trialing: {
        variant: 'secondary',
        label: t('subscription.status.trialing'),
      },
      past_due: {
        variant: 'destructive',
        label: t('subscription.status.pastDue'),
      },
      canceled: {
        variant: 'outline',
        label: t('subscription.status.canceled'),
      },
      incomplete: {
        variant: 'secondary',
        label: t('subscription.status.incomplete'),
      },
    };

    const config = variants[status] || { variant: 'outline', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const handleOpenBillingPortal = async () => {
    try {
      const result = await portalMutation.mutateAsync(window.location.href);
      window.open(result.url, '_blank');
    } catch (err) {
      toast({
        title: t('subscription.error'),
        description: t('subscription.portalError'),
        variant: 'destructive',
      });
    }
  };

  const handleUpdateSubscription = async () => {
    if (!newGymCount || newGymCount < 1) return;

    try {
      await updateMutation.mutateAsync({ newGymCount });
      toast({
        title: t('subscription.success'),
        description: t('subscription.updateSuccess'),
      });
      setShowUpgradeDialog(false);
    } catch (err) {
      toast({
        title: t('subscription.error'),
        description:
          err instanceof Error ? err.message : t('subscription.updateError'),
        variant: 'destructive',
      });
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await cancelMutation.mutateAsync({
        immediate: cancelImmediate,
        reason: cancelReason,
      });
      toast({
        title: t('subscription.success'),
        description: cancelImmediate
          ? t('subscription.canceledImmediately')
          : t('subscription.canceledAtPeriodEnd'),
      });
      setShowCancelDialog(false);
    } catch (err) {
      toast({
        title: t('subscription.error'),
        description:
          err instanceof Error ? err.message : t('subscription.cancelError'),
        variant: 'destructive',
      });
    }
  };

  const handleReactivate = async () => {
    try {
      await reactivateMutation.mutateAsync();
      toast({
        title: t('subscription.success'),
        description: t('subscription.reactivated'),
      });
    } catch (err) {
      toast({
        title: t('subscription.error'),
        description:
          err instanceof Error
            ? err.message
            : t('subscription.reactivateError'),
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-10 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error || !subscription) {
    return (
      <Card>
        <CardContent className="py-10">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{t('subscription.loadError')}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const pricePerGym = pricing?.pricePerGym || subscription.pricePerGym || 100;
  const monthlyTotal = subscription.gymLimit * pricePerGym;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {t('subscription.title')}
              </CardTitle>
              <CardDescription>{t('subscription.description')}</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('subscription.refresh')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status Banner */}
          {subscription.status === 'past_due' && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {t('subscription.pastDueWarning')}
                <Button
                  variant="link"
                  className="p-0 h-auto ml-2"
                  onClick={handleOpenBillingPortal}
                >
                  {t('subscription.updatePayment')}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {subscription.cancelAtPeriodEnd && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {t('subscription.cancelScheduled', {
                  date: format(new Date(subscription.currentPeriodEnd), 'PPP'),
                })}
                <Button
                  variant="link"
                  className="p-0 h-auto ml-2"
                  onClick={handleReactivate}
                  disabled={reactivateMutation.isPending}
                >
                  {t('subscription.reactivate')}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Subscription Details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                {t('subscription.status.label')}
              </p>
              {getStatusBadge(subscription.status)}
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                {t('subscription.plan')}
              </p>
              <p className="font-medium capitalize">{subscription.plan}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                {t('subscription.gymLimit')}
              </p>
              <p className="font-medium">
                {subscription.gymCount} / {subscription.gymLimit}{' '}
                {t('subscription.gyms')}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                {t('subscription.nextBilling')}
              </p>
              <p className="font-medium">
                {format(new Date(subscription.currentPeriodEnd), 'PPP')}
              </p>
            </div>
          </div>

          <Separator />

          {/* Billing Summary */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">
                {t('subscription.monthlyTotal')}
              </p>
              <p className="text-2xl font-bold">
                €{monthlyTotal}
                <span className="text-sm font-normal text-muted-foreground">
                  /{t('subscription.month')}
                </span>
              </p>
            </div>
            <div className="text-sm text-muted-foreground text-right">
              <p>
                {subscription.gymLimit} {t('subscription.gyms')} × €
                {pricePerGym}
              </p>
            </div>
          </div>

          <Separator />

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setNewGymCount(subscription.gymLimit);
                setShowUpgradeDialog(true);
              }}
              disabled={subscription.status === 'canceled'}
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              {t('subscription.changeGyms')}
            </Button>

            <Button variant="outline" onClick={handleOpenBillingPortal}>
              <CreditCard className="h-4 w-4 mr-2" />
              {t('subscription.manageBilling')}
              <ExternalLink className="h-3 w-3 ml-2" />
            </Button>

            {subscription.status !== 'canceled' &&
              !subscription.cancelAtPeriodEnd && (
                <Button
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setShowCancelDialog(true)}
                >
                  <X className="h-4 w-4 mr-2" />
                  {t('subscription.cancel')}
                </Button>
              )}
          </div>
        </CardContent>
      </Card>

      {/* Upgrade/Downgrade Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('subscription.changeGymsTitle')}</DialogTitle>
            <DialogDescription>
              {t('subscription.changeGymsDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{t('subscription.newGymCount')}</Label>
              <Select
                value={String(newGymCount)}
                onValueChange={(val) => setNewGymCount(parseInt(val))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 10, 15, 20, 30, 50].map((num) => (
                    <SelectItem key={num} value={String(num)}>
                      {num}{' '}
                      {num === 1
                        ? t('subscription.gym')
                        : t('subscription.gyms')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-muted p-4 rounded-lg">
              <div className="flex justify-between mb-2">
                <span>{t('subscription.currentPlan')}</span>
                <span>
                  {subscription.gymLimit} {t('subscription.gyms')} - €
                  {subscription.gymLimit * pricePerGym}/
                  {t('subscription.month')}
                </span>
              </div>
              <div className="flex justify-between font-bold">
                <span>{t('subscription.newPlan')}</span>
                <span>
                  {newGymCount} {t('subscription.gyms')} - €
                  {newGymCount * pricePerGym}/{t('subscription.month')}
                </span>
              </div>
              {newGymCount !== subscription.gymLimit && (
                <div className="flex items-center gap-2 mt-2 text-sm">
                  {newGymCount > subscription.gymLimit ? (
                    <>
                      <TrendingUp className="h-4 w-4 text-green-500" />
                      <span className="text-green-600">
                        {t('subscription.upgrading')}
                      </span>
                    </>
                  ) : (
                    <>
                      <TrendingDown className="h-4 w-4 text-orange-500" />
                      <span className="text-orange-600">
                        {t('subscription.downgrading')}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowUpgradeDialog(false)}
            >
              {t('subscription.cancel')}
            </Button>
            <Button
              onClick={handleUpdateSubscription}
              disabled={
                updateMutation.isPending ||
                newGymCount === subscription.gymLimit
              }
            >
              {updateMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t('subscription.confirmChange')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('subscription.cancelTitle')}</DialogTitle>
            <DialogDescription>
              {t('subscription.cancelDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {t('subscription.cancelWarning')}
              </AlertDescription>
            </Alert>

            <div>
              <Label>{t('subscription.cancelReason')}</Label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder={t('subscription.cancelReasonPlaceholder')}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="cancelImmediate"
                checked={cancelImmediate}
                onChange={(e) => setCancelImmediate(e.target.checked)}
              />
              <Label htmlFor="cancelImmediate" className="text-sm">
                {t('subscription.cancelImmediately')}
              </Label>
            </div>
            {!cancelImmediate && (
              <p className="text-sm text-muted-foreground">
                {t('subscription.cancelAtPeriodEndNote', {
                  date: format(new Date(subscription.currentPeriodEnd), 'PPP'),
                })}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
            >
              {t('subscription.keepSubscription')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t('subscription.confirmCancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
